import styled from "styled-components";

import { Modal, type ModalProps } from "../Modal";

type MediaOverlayBaseProps = Omit<
  ModalProps,
  "size" | "maxHeight" | "aria-label" | "aria-labelledby"
>;

type MediaOverlayAccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: never; "aria-labelledby": string };

/**
 * Frameless modal lightbox for camera, video, heatmap, and image content.
 *
 * `MediaOverlay` owns only the full-viewport modal shell. Media dimensions/aspect ratio, playback
 * controls, zoom/pan, and domain actions remain consumer composition in `children`. The shell cap
 * does not resize descendants; viewport-fitted media should use the leaf `maxHeight` seam.
 */
export type MediaOverlayProps = MediaOverlayBaseProps & MediaOverlayAccessibleName;

// Modal owns portal/focus/inert/scroll-lock/dismiss/motion/stacking. This composition changes only
// the Dialog presentation. Keeping overflow visible lets nested Tooltip/AnchoredPopup portal roots,
// which modalStack mounts in this Dialog, escape a consumer media surface's clipping boundary.
const FramelessModal = styled(Modal)`
  width: fit-content;
  min-width: 0;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  overflow: visible;
  background-color: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;

  /* A transparent dialog can sit above arbitrary page/media pixels, so foreground-only overrides
     cannot guarantee caption contrast. Descendant VideoCaption keeps its own DOM/state, while the
     overlay supplies an opaque semantic chrome surface and readable foreground through inherited
     internal variables. Inline captions keep their historical transparent presentation. */
  --c6-media-caption-surface: ${({ theme }) => theme.color.bg.surfaceOverlay};
  --c6-media-caption-color: ${({ theme }) => theme.color.text.default};
  --c6-media-caption-subtle-color: ${({ theme }) => theme.color.text.default};
  --c6-media-caption-padding: 0.25rem 0.4rem;
  --c6-media-caption-radius: ${({ theme }) => theme.shape.radius.surface};
`;

/**
 * Media lightbox backed by the c6 Modal lifecycle and nested-overlay mount contract.
 *
 * The default `level="media"` keeps expanded media below IO locks and ordinary dialogs.
 * Provide an accessible name with `aria-label` or `aria-labelledby`.
 */
export function MediaOverlay({ level = "media", ...props }: MediaOverlayProps) {
  return <FramelessModal {...props} level={level} size="lg" />;
}
